import { useTranslation } from 'react-i18next'

interface AuditEvent {
  evento: string
  createdAt: string
  signerId?: string
  signerName?: string
  metadataJson?: Record<string, unknown>
}

interface Props {
  events: AuditEvent[]
}

interface EventMeta {
  icon: string
  title: string
  description: (e: AuditEvent) => string
  dot: string
  bg: string
  text: string
}

const EVENT_META: Record<string, EventMeta> = {
  DOCUMENT_CREATED: {
    icon: '📄',
    title: 'Documento criado',
    description: () => 'O documento foi criado e está aguardando envio aos signatários.',
    dot: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-800',
  },
  DOCUMENT_SENT: {
    icon: '📨',
    title: 'Links enviados',
    description: (e) => e.signerName
      ? `Link de assinatura enviado para ${e.signerName}.`
      : 'Links de assinatura enviados a todos os signatários.',
    dot: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-800',
  },
  LINK_ACCESSED: {
    icon: '🔗',
    title: 'Link acessado',
    description: (e) => e.signerName
      ? `${e.signerName} abriu o link de assinatura.`
      : 'O link de assinatura foi acessado.',
    dot: 'bg-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-800',
  },
  DOCUMENT_VIEWED: {
    icon: '👁️',
    title: 'Documento visualizado',
    description: (e) => e.signerName
      ? `${e.signerName} leu o documento.`
      : 'O documento foi lido pelo signatário.',
    dot: 'bg-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-800',
  },
  SELFIE_INITIATED: {
    icon: '📷',
    title: 'Verificação de identidade iniciada',
    description: (e) => e.signerName
      ? `${e.signerName} iniciou a captura de selfie para verificação.`
      : 'Verificação de identidade por selfie iniciada.',
    dot: 'bg-purple-400', bg: 'bg-purple-50', text: 'text-purple-800',
  },
  SELFIE_CAPTURED: {
    icon: '🤳',
    title: 'Selfie capturada',
    description: (e) => e.signerName
      ? `Selfie de ${e.signerName} registrada com sucesso para verificação de identidade.`
      : 'Selfie capturada e registrada com sucesso.',
    dot: 'bg-purple-500', bg: 'bg-purple-50', text: 'text-purple-800',
  },
  PAYMENT_GENERATED: {
    icon: '💳',
    title: 'Cobrança gerada',
    description: (e) => e.signerName
      ? `Cobrança de verificação bancária gerada para ${e.signerName}.`
      : 'Cobrança de verificação bancária gerada.',
    dot: 'bg-orange-400', bg: 'bg-orange-50', text: 'text-orange-800',
  },
  PAYMENT_CONFIRMED: {
    icon: '✅',
    title: 'Pagamento confirmado',
    description: (e) => e.signerName
      ? `Pagamento de ${e.signerName} confirmado — identidade bancária verificada pelo banco emissor.`
      : 'Pagamento confirmado e identidade bancária verificada.',
    dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-800',
  },
  PAYMENT_EXPIRED: {
    icon: '⏰',
    title: 'Pagamento expirado',
    description: (e) => e.signerName
      ? `O prazo para pagamento de ${e.signerName} expirou sem confirmação.`
      : 'O prazo de pagamento expirou sem confirmação.',
    dot: 'bg-red-400', bg: 'bg-red-50', text: 'text-red-800',
  },
  IDENTITY_MISMATCH: {
    icon: '🚫',
    title: 'Identidade não confirmada',
    description: (e) => e.signerName
      ? `Os dados de ${e.signerName} no pagamento não coincidem com os dados cadastrados. Assinatura bloqueada.`
      : 'Os dados do pagamento não coincidem com os dados do signatário. Assinatura bloqueada.',
    dot: 'bg-red-600', bg: 'bg-red-50', text: 'text-red-800',
  },
  SIGNATURE_STARTED: {
    icon: '✍️',
    title: 'Assinatura em andamento',
    description: (e) => e.signerName
      ? `${e.signerName} está finalizando a assinatura do documento.`
      : 'O signatário está finalizando a assinatura.',
    dot: 'bg-indigo-400', bg: 'bg-indigo-50', text: 'text-indigo-800',
  },
  SIGNATURE_COMPLETED: {
    icon: '🖊️',
    title: 'Documento assinado',
    description: (e) => e.signerName
      ? `${e.signerName} assinou o documento com sucesso.`
      : 'O documento foi assinado com sucesso.',
    dot: 'bg-green-600', bg: 'bg-green-50', text: 'text-green-800',
  },
  DOCUMENT_FINALIZED: {
    icon: '🏁',
    title: 'Documento finalizado',
    description: () => 'Todos os signatários completaram o processo. O PDF final com certificado de assinatura PAdES foi gerado.',
    dot: 'bg-green-700', bg: 'bg-green-100', text: 'text-green-900',
  },
  DOCUMENT_DOWNLOADED: {
    icon: '⬇️',
    title: 'PDF baixado',
    description: (e) => e.signerName
      ? `${e.signerName} baixou o PDF assinado.`
      : 'O PDF assinado foi baixado.',
    dot: 'bg-gray-500', bg: 'bg-gray-50', text: 'text-gray-800',
  },
  VERIFICATION_ACCESSED: {
    icon: '🔍',
    title: 'Autenticidade verificada',
    description: () => 'O certificado de autenticidade do documento foi consultado.',
    dot: 'bg-gray-400', bg: 'bg-gray-50', text: 'text-gray-700',
  },
}

const DEFAULT_META: EventMeta = {
  icon: '📋',
  title: '',
  description: (e) => e.evento.replace(/_/g, ' ').toLowerCase(),
  dot: 'bg-gray-400', bg: 'bg-gray-50', text: 'text-gray-700',
}

function formatTimestamp(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(iso))
}

export default function AuditTimeline({ events }: Props) {
  const { t } = useTranslation()

  if (events.length === 0) {
    return (
      <div className="text-sm text-gray-400 text-center py-8">
        {t('auditTimeline.empty', 'Nenhum evento registrado.')}
      </div>
    )
  }

  return (
    <div className="flow-root">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        {t('auditTimeline.title', 'Trilha de auditoria')}
      </h3>
      <ul className="-mb-6">
        {events.map((event, idx) => {
          const meta = EVENT_META[event.evento] ?? DEFAULT_META
          const isLast = idx === events.length - 1
          const title = meta.title || event.evento.replace(/_/g, ' ')
          const description = meta.description(event)

          return (
            <li key={`${event.evento}-${event.createdAt}-${idx}`}>
              <div className="relative pb-6">
                {!isLast && (
                  <span className="absolute left-4 top-8 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                )}
                <div className="relative flex items-start gap-3">
                  {/* Icon dot */}
                  <div className="flex-shrink-0 mt-0.5">
                    <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${meta.dot} ring-4 ring-white text-sm`}>
                      {meta.icon}
                    </span>
                  </div>

                  {/* Content */}
                  <div className={`flex-1 min-w-0 rounded-xl px-4 py-3 ${meta.bg}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className={`text-sm font-semibold ${meta.text}`}>{title}</span>
                      <time className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                        {formatTimestamp(event.createdAt)}
                      </time>
                    </div>
                    <p className="mt-0.5 text-sm text-gray-600 leading-relaxed">{description}</p>

                    {event.metadataJson && Object.keys(event.metadataJson).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                          {t('auditTimeline.metadata', 'Detalhes técnicos')}
                        </summary>
                        <pre className="mt-1 text-xs text-gray-500 bg-white/60 rounded p-2 overflow-x-auto">
                          {JSON.stringify(event.metadataJson, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
